import React, { Children, useMemo } from "react";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import { Spinner } from "../../../../../Components";
import { makeStyles, TableFooter } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import Row from "./CollapsibleTableRow"; // Ensure path is correct
import { PerformanceInsightsTranslationPath } from "../../i18n/PerformanceInsightsLocale";
import { formatNumberWithCommas } from "../../utilities/helpers";

const FallbackRow = ({ row }) => (
  <TableRow>
    <TableCell colSpan={8}>
      Error: Row component not found for {row.propertyName}
    </TableCell>
  </TableRow>
);

const useStyles = makeStyles({
  rowBody: {
    "& .MuiTableCell-root": {
      maxWidth: "200px",
    },
    "& .MuiTableCell-head": {
      fontWeight: "bold",
      color: "#4b5563",
    },
    "& .MuiTableCell-footer": {
      fontWeight: "bold",
      color: "#4b5563",
    },
  },
  tableHead: {
    background: "#f3f4f6",
  },
});

export default function CollapsibleTable({ rows = [], loading, params }) {
  const { t } = useTranslation(PerformanceInsightsTranslationPath);
  const classes = useStyles();

  const filteredRows = useMemo(() => {
    if (!params?.mediaTypes || params.mediaTypes.length === 0) {
      return rows.map((property) => ({
        ...property,
        campaigns: property.campaigns.map((campaign) => ({
          ...campaign,
          mediaDetails: campaign.mediaDetails ?? "",
          totalLeads: campaign.totalLeads ?? 0,
        })),
      }));
    }

    return rows
      .map((property) => ({
        ...property,
        campaigns: property.campaigns.filter((campaign) =>
          params.mediaTypes.includes(campaign.mediaDetails || "")
        ),
      }))
      .filter((property) => property.campaigns.length > 0)
      .map((property) => ({
        ...property,
        campaigns: property.campaigns.map((campaign) => ({
          ...campaign,
          mediaDetails: campaign.mediaDetails ?? "",
          totalLeads: campaign.totalLeads ?? 0,
        })),
      }));
  }, [rows, params?.mediaTypes]);

  const total = useMemo(() => {
    return filteredRows.reduce(
      (acc, property) => {
        acc.totalLeads += property.totalLeadsByProperty || 0;
        property.campaigns.forEach((campaign) => {
          acc.activeLeads += campaign.activeLeads || 0;
          acc.qualifiedLeads += campaign.qualifiedLeads || 0;
          acc.unqualifiedLeads += campaign.unqualifiedLeads || 0;
          acc.leadView += campaign.totalLeads || 0;
        });
        return acc;
      },
      {
        totalLeads: 0,
        activeLeads: 0,
        qualifiedLeads: 0,
        unqualifiedLeads: 0,
        leadView: 0,
      }
    );
  }, [filteredRows]);

  const TableRowComponent = typeof Row === "undefined" ? FallbackRow : Row;

  return (
    <TableContainer component={Paper}>
      <Spinner isAbsolute isActive={loading} />
      <Table aria-label="collapsible table">
        <TableHead className={classes.tableHead}>
          <TableRow className={classes.rowBody}>
            <TableCell>{t("campaign_id")}</TableCell>
            <TableCell>{t("Campaign")}</TableCell>
            <TableCell>{t("platform")}</TableCell>
            <TableCell>{t("media_details")}</TableCell>
            <TableCell>{t("ActiveLeads")}</TableCell>
            <TableCell>{t("qualified")}</TableCell>
            <TableCell>{t("unqualified")}</TableCell>
            <TableCell>{t("lead_view")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Children.toArray(
            filteredRows.map((row) => (
              <TableRowComponent key={row.propertyName} row={row} />
            ))
          )}
        </TableBody>
        <TableFooter className={classes.tableHead}>
          <TableRow className={classes.rowBody}>
            <TableCell>{t("grand_total")}</TableCell>
            <TableCell colSpan={3} />
            <TableCell>{formatNumberWithCommas(total.activeLeads)}</TableCell>
            <TableCell>
              {formatNumberWithCommas(total.qualifiedLeads)}
            </TableCell>
            <TableCell>
              {formatNumberWithCommas(total.unqualifiedLeads)}
            </TableCell>
            <TableCell>{formatNumberWithCommas(total.leadView)}</TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
